<template>
  <div class="py-4 container-fluid">
    <div class="card">
      <div class="card-header pb-0">
        <div class="d-flex align-items-center mb-3">
          <h6 class="mb-0">Shopify Vzla — Inventario</h6>
          <argon-button
            color="info"
            size="sm"
            class="ms-auto"
            :disabled="isSyncing"
            @click="syncProducts"
          >
            <span v-if="isSyncing">
              <i class="fas fa-spinner fa-spin me-1"></i>
              Sincronizando Kubapp...
            </span>
            <span v-else>
              <i class="fas fa-sync me-1"></i> Sincronizar Kubapp
            </span>
          </argon-button>
        </div>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="searchQuery" class="mb-1">Buscar</label>
            <input
              id="searchQuery"
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Buscar por título, handle, vendor o ID..."
              @input="onSearchInput"
            />
          </div>
        </div>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div v-if="isLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-1"></i>
          Cargando inventario...
        </div>

        <p v-else-if="!products.length" class="text-muted px-4 mb-0">
          No hay productos sincronizados. Pulsa Sincronizar Kubapp para traerlos desde Kubapp.
        </p>

        <div v-else class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">
                  ID Kubapp
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Producto
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Estado
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Código
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Variantes
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Stock
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Costo
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Precio usd
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Precio
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Grupo
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Sincronizado
                </th>
                <th v-if="shopifyVeEnabled" class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Opciones
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in products" :key="product.id">
                <td class="ps-4">
                  <span class="text-sm">{{ product.shopify_product_id }}</span>
                </td>
                <td>
                  <div class="d-flex align-items-center">
                    <div class="material-img-cell position-relative me-2">
                      <img
                        v-if="product.image_url"
                        :src="resolveImageUrl(product.image_url)"
                        :alt="product.title"
                        class="avatar avatar-sm material-img-thumb"
                        @click="triggerFileInput(product)"
                      />
                      <button
                        v-else
                        class="btn btn-sm btn-outline-secondary material-img-btn"
                        title="Subir imagen"
                        @click="triggerFileInput(product)"
                      >
                        <i class="fas fa-image"></i>
                      </button>
                      <input
                        :ref="setFileInputRef(product.id)"
                        type="file"
                        accept="image/*"
                        class="d-none"
                        @change="onFileSelected($event, product)"
                      />
                      <div
                        v-if="uploadingId === product.id"
                        class="material-img-overlay"
                      >
                        <i class="fas fa-spinner fa-spin"></i>
                      </div>
                    </div>
                    <div>
                      <span class="text-sm font-weight-bold d-block">{{ product.title }}</span>
                      <span v-if="product.vendor" class="text-xs text-secondary">{{ product.vendor }}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <span
                    class="badge badge-sm"
                    :class="product.status === 'active' ? 'bg-gradient-success' : 'bg-gradient-secondary'"
                  >
                    {{ product.status }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ product.codigo_prod || '—' }}</span>
                </td>
                <td class="text-center">
                  <span
                    class="text-sm variants-count-link"
                    title="Ver variantes"
                    @click="openVariantsModal(product)"
                  >
                    {{ product.variants_count }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ product.inventory_quantity }}</span>
                </td>
                <td class="text-center">
                  <div v-if="isAdmin" class="d-inline-flex align-items-center">
                    <input
                      v-model="product.cost"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control form-control-sm price-input"
                      placeholder="0.00"
                      :disabled="savingPriceId === product.id"
                      @change="savePricing(product)"
                    />
                    <i
                      v-if="savingPriceId === product.id"
                      class="fas fa-spinner fa-spin ms-1 text-secondary"
                    ></i>
                  </div>
                  <span v-else class="text-sm">{{ product.cost || '—' }}</span>
                </td>
                <td class="text-center">
                  <div v-if="isAdmin" class="d-inline-flex align-items-center">
                    <input
                      v-model="product.price_usd"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control form-control-sm price-input"
                      placeholder="0.00"
                      :disabled="savingPriceId === product.id"
                      @change="savePricing(product)"
                    />
                    <i
                      v-if="savingPriceId === product.id"
                      class="fas fa-spinner fa-spin ms-1 text-secondary"
                    ></i>
                  </div>
                  <span v-else class="text-sm">{{ product.price_usd || '—' }}</span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ formatPrice(product.price_min) }}</span>
                </td>
                <td class="text-center">
                  <span class="text-xs text-secondary">{{ product.product_type || '—' }}</span>
                </td>
                <td class="text-center">
                  <span class="text-xs text-secondary">{{ formatDate(product.synced_at) }}</span>
                </td>
                <td v-if="shopifyVeEnabled" class="text-center">
                  <div class="d-inline-flex align-items-center opciones-icons">
                    <span class="badge badge-sm bg-gradient-warning me-1" title="Venezuela">VZL</span>
                    <button
                      class="btn btn-sm btn-link p-0 text-success opciones-icon-btn"
                      title="Sincronizar con Shopify (VZL)"
                      :disabled="pushingId === product.id"
                      @click="pushProductToShopify(product)"
                    >
                      <i v-if="pushingId === product.id" class="fas fa-spinner fa-spin"></i>
                      <i v-else class="fab fa-shopify"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="pagination p-3">
            <a
              v-for="link in links"
              :key="link.label"
              class="page-item cursor-pointer"
              :class="{ active: link.active, disabled: !link.url }"
              @click="link.url && fetchInventoryFromLink(link.url)"
            >
              {{ link.label }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Ver Variantes -->
    <div v-if="showVariantsModal" class="picking-modal-overlay" @click.self="closeVariantsModal">
      <div class="picking-modal-box" style="max-width: 600px;">
        <div class="picking-modal-header">
          <h5 class="mb-0">
            <i class="fas fa-eye me-2 text-info"></i>
            Variantes
          </h5>
          <button class="btn btn-link btn-sm p-0 text-secondary" @click="closeVariantsModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="picking-modal-body">
          <p class="text-sm font-weight-bold mb-2">{{ variantsProduct?.title || '—' }}</p>
          <div v-if="variantsProduct?.variants?.length" class="table-responsive">
            <table class="table table-sm align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Título</th>
                  <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">SKU</th>
                  <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Barcode</th>
                  <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Precio</th>
                  <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Stock</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(variant, idx) in variantsProduct.variants" :key="idx">
                  <td class="text-sm">{{ variant.title || '—' }}</td>
                  <td class="text-center text-sm">{{ variant.sku || '—' }}</td>
                  <td class="text-center text-sm">{{ variant.barcode || '—' }}</td>
                  <td class="text-center text-sm">{{ variant.price || '0.00' }}</td>
                  <td class="text-center text-sm">{{ variant.inventory_quantity ?? '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-muted text-sm text-center py-3">Sin variantes.</p>
        </div>
        <div class="picking-modal-footer">
          <button class="btn btn-secondary btn-sm" @click="closeVariantsModal">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import shopifyVeService from "@/services/shopifyVenezuela.service";
import Swal from "sweetalert2";

export default {
  name: "ShopifyVeInventory",
  components: {
    ArgonButton,
  },
  data() {
    return {
      isSyncing: false,
      pushingId: null,
      isLoading: false,
      products: [],
      links: [],
      currentPage: 1,
      lastPage: 1,
      total: 0,
      searchQuery: "",
      searchTimeout: null,
      uploadingId: null,
      fileInputRefs: {},
      showVariantsModal: false,
      variantsProduct: null,
      savingPriceId: null,
    };
  },
  computed: {
    shopifyVeEnabled() {
      return this.$store.getters["settings/shopifyVeEnabled"];
    },
    isAdmin() {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.role === "1" || user?.role === 1;
      } catch {
        return false;
      }
    },
  },
  mounted() {
    this.fetchInventory();
  },
  methods: {
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchInventory(1);
      }, 400);
    },
    async fetchInventory(page = 1) {
      this.isLoading = true;
      try {
        const response = await shopifyVeService.getInventory({
          page,
          search: this.searchQuery,
          per_page: 15,
        });
        const data = response?.data || {};

        this.products = data.data || [];
        this.links = data.links || [];
        this.currentPage = data.current_page || 1;
        this.lastPage = data.last_page || 1;
        this.total = data.total || 0;
      } catch (error) {
        console.error("Error al cargar inventario Venezuela:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudo cargar el inventario.",
          "error"
        );
      } finally {
        this.isLoading = false;
      }
    },
    fetchInventoryFromLink(url) {
      const page = new URL(url).searchParams.get("page") || 1;
      this.fetchInventory(Number(page));
    },
    async syncProducts() {
      this.isSyncing = true;
      try {
        const response = await shopifyVeService.syncProducts();
        const data = response?.data || {};

        if (data.success) {
          const sync = data.sync || {};
          await this.fetchInventory(1);
          await Swal.fire(
            "Sincronizado",
            `Se procesaron ${sync.total ?? 0} productos (${sync.created ?? 0} nuevos, ${sync.updated ?? 0} actualizados).`,
            "success"
          );
        } else {
          throw new Error(data.message || "No se pudo sincronizar.");
        }
      } catch (error) {
        console.error("Error al sincronizar Kubapp:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al sincronizar con Kubapp.",
          "error"
        );
      } finally {
        this.isSyncing = false;
      }
    },
    async pushProductToShopify(product) {
      const priceUsd = parseFloat(product.price_usd);
      if (isNaN(priceUsd) || priceUsd <= 0) {
        await Swal.fire(
          "Precio USD requerido",
          `«${product.title}» no tiene un precio USD válido. Define el precio USD antes de sincronizar con Shopify.`,
          "warning"
        );
        return;
      }

      this.pushingId = product.id;
      try {
        const response = await shopifyVeService.pushProductToShopify(product.id);
        const data = response?.data || {};

        if (data.success) {
          const action = data.action || "sincronizado";
          await Swal.fire({
            icon: "success",
            title: "Sincronizado con Shopify",
            text: `«${product.title}» fue ${action} correctamente en Shopify.`,
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          throw new Error(data.message || "No se pudo sincronizar con Shopify.");
        }
      } catch (error) {
        console.error("Error al sincronizar producto con Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al sincronizar con Shopify.",
          "error"
        );
      } finally {
        this.pushingId = null;
      }
    },
    async savePricing(product) {
      this.savingPriceId = product.id;
      try {
        const payload = {
          cost: product.cost === "" || product.cost === null ? null : Number(product.cost),
          price_usd: product.price_usd === "" || product.price_usd === null ? null : Number(product.price_usd),
        };
        const response = await shopifyVeService.updateInventory(product.id, payload);
        const data = response?.data || {};

        if (!data.success) {
          throw new Error(data.message || "No se pudo guardar.");
        }
      } catch (error) {
        console.error("Error al guardar costo/precio USD:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo guardar el producto.",
          "error"
        );
      } finally {
        this.savingPriceId = null;
      }
    },
    openVariantsModal(product) {
      this.variantsProduct = product;
      this.showVariantsModal = true;
    },
    closeVariantsModal() {
      this.showVariantsModal = false;
      this.variantsProduct = null;
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }

      return new Date(value).toLocaleString("es-CO", {
        dateStyle: "short",
        timeStyle: "short",
      });
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }
      const num = parseFloat(value);
      if (isNaN(num) || num === 0) {
        return '—';
      }
      return '$' + num.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    setFileInputRef(productId) {
      return (el) => {
        if (el) this.fileInputRefs[productId] = el;
      };
    },
    triggerFileInput(product) {
      const input = this.fileInputRefs[product.id];
      if (input) {
        input.value = "";
        input.click();
      }
    },
    resolveImageUrl(url) {
      if (!url) return null;
      if (url.startsWith("http")) return url;
      const base = (process.env.VUE_APP_API_BASE_URL || "").replace(/\/$/, "");
      return base.replace(/\/api$/, "") + url;
    },
    async onFileSelected(event, product) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.size > 4 * 1024 * 1024) {
        await Swal.fire("Imagen demasiado grande", "Máximo 4 MB.", "warning");
        return;
      }

      this.uploadingId = product.id;
      try {
        const response = await shopifyVeService.uploadProductImage(product.id, file);
        const data = response?.data || {};

        if (data.success) {
          const newUrl = data.image_url;
          const target = this.products.find((p) => p.id === product.id);
          if (target) target.image_url = newUrl;
          await Swal.fire({
            icon: "success",
            title: "Imagen actualizada",
            timer: 1200,
            showConfirmButton: false,
          });
        } else {
          throw new Error(data.message || "No se pudo subir la imagen.");
        }
      } catch (error) {
        console.error("Error al subir imagen:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo subir la imagen.",
          "error"
        );
      } finally {
        this.uploadingId = null;
      }
    },
  },
};
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
}

.page-item {
  margin: 0 5px;
  padding: 4px 8px;
  user-select: none;
}

.page-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.active {
  font-weight: bold;
}

.material-img-cell {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.material-img-thumb {
  cursor: pointer;
  object-fit: cover;
  border-radius: 0.375rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.material-img-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-bottom: 0 !important;
  border-radius: 0.375rem;
}

.material-img-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
}

.price-input {
  width: 110px;
  text-align: right;
}

.variants-count-link {
  cursor: pointer;
  color: #5e72e4;
  font-weight: bold;
}

/* Iconos columna Opciones */
.opciones-icons {
  gap: 12px;
  vertical-align: middle;
}

.opciones-icon-btn {
  margin-bottom: 0 !important;
  line-height: 1;
  font-size: 1rem;
}

/* Modal variantes */
.picking-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.picking-modal-box {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.picking-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.picking-modal-body {
  padding: 20px;
  overflow-y: auto;
}

.picking-modal-footer {
  padding: 12px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
